import { FC, PropsWithChildren, ReactNode, useMemo } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { CallIntelUtils } from '@frontend/api-call-intel';
import { NoTranscription } from '@frontend/assets';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { transcribingCallLottie } from '../assets/lotties';
import { CallFlagsData, CallInsightBaseProps } from '../types';
import { callStatusUtils } from '../utils/call-intel-utils';
import { CallResponder } from './call-responder';
import { CallEmptyState } from './empty-state/call-empty-state';
import { AnalyzingCallLoader } from './loaders/analyzing-call-loader';
import { PrivateFeature } from './private-feature';

export interface CallSummaryProps extends CallInsightBaseProps {
  containerStyles?: Interpolation<Theme>;
  callFlags?: CallFlagsData;
}

const CallSummaryWrapper: FC<{ children: ReactNode; containerStyles?: Interpolation<Theme> }> = ({
  children,
  containerStyles,
}) => (
  <div css={[styles.containerStyles, containerStyles]} data-testid='call-summary'>
    {children}
  </div>
);

export const CallSummary: FC<PropsWithChildren<CallSummaryProps>> = ({
  call,
  callFlags,
  callMetadata,
  children,
  containerStyles,
  isLoading,
  isDemoAccount,
}) => {
  const { t } = useTranslation('call-intel-core');
  const { isFailedCall, isProcessingCall, isSkippedCall, isPrivateCall } =
    callFlags ?? callStatusUtils.getCallFlags(call);

  const callSummary = useMemo(() => {
    if (!callMetadata?.summary) return '';

    return !isDemoAccount
      ? callMetadata.summary
      : CallIntelUtils.parseTranscriptWithTokens(callMetadata.summary, {
          PERSON_NAME: call?.person?.firstName ?? '',
        });
  }, [callMetadata?.summary, isDemoAccount, call?.person?.firstName]);

  const showLoading = isProcessingCall || isLoading;
  const showEmptyState = !isPrivateCall && (isFailedCall || isSkippedCall || !callMetadata?.summary);

  if (showLoading) {
    return (
      <CallSummaryWrapper containerStyles={containerStyles}>
        <AnalyzingCallLoader lottie={transcribingCallLottie} message={t('Processing phone call...')} />
      </CallSummaryWrapper>
    );
  }

  if (isPrivateCall) {
    return (
      <CallSummaryWrapper containerStyles={containerStyles}>
        <PrivateFeature />
      </CallSummaryWrapper>
    );
  }

  if (showEmptyState) {
    return (
      <CallSummaryWrapper containerStyles={containerStyles}>
        <CallEmptyState
          mainText={t('No Summary Available')}
          style={{ overflow: 'auto' }}
          subText={t('Please review the call recording.')}
          svg={NoTranscription}
        />
      </CallSummaryWrapper>
    );
  }

  return (
    <CallSummaryWrapper containerStyles={containerStyles}>
      <div css={styles.content}>
        <CallResponder officeUser={call?.officeUser} />
        <p css={styles.summary}>
          <Text as='span' size='medium'>
            {callSummary}
          </Text>
        </p>
        {children}
      </div>
    </CallSummaryWrapper>
  );
};

const styles = {
  containerStyles: css`
    display: flex;
    flex-direction: column;
    overflow: auto;
  `,
  content: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    overflow: auto;
    position: relative;
  `,
  summary: css`
    display: flex;
    flex-direction: column;
    overflow: auto;
    padding-right: ${theme.spacing(2)};

    &::-webkit-scrollbar {
      -webkit-appearance: none;
      width: ${theme.spacing(1)};
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${theme.colors.neutral30};
      border-radius: ${theme.borderRadius.small};
    }
  `,

  officeUserName: css`
    display: flex;
    gap: ${theme.spacing(0.5)};
  `,
};
